import React, { useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import { States } from '../../__generated__/types';

// Declare Google Maps API types
declare global {
  interface Window {
    google: any;
    initGooglePlaces: () => void;
  }
}

interface AddressComponents {
  streetNumber?: string;
  route?: string;
  locality?: string; // city
  administrativeAreaLevel1?: string; // state
  postalCode?: string;
  country?: string;
  formattedAddress?: string;
}

interface AddressAutocompleteProps extends Omit<TextFieldProps, 'onChange'> {
  onAddressSelect: (addressComponents: AddressComponents) => void;
  onInputChange?: (value: string) => void;
  placeholder?: string;
  apiKey?: string;
}

// Google Maps API key
const GOOGLE_MAPS_API_KEY = 'AIzaSyA3OEL-k1KpEuLj9anC9i-ZivP3ODeVthw';

// US State code to States enum mapping
// The States enum uses abbreviated property names like Al, Ak, Ca, etc.
const STATE_CODE_TO_STATES_ENUM: { [key: string]: States } = {
  'AL': States.Al, 'AK': States.Ak, 'AZ': States.Az, 'AR': States.Ar,
  'CA': States.Ca, 'CO': States.Co, 'CT': States.Ct, 'DE': States.De,
  'FL': States.Fl, 'GA': States.Ga, 'HI': States.Hi, 'ID': States.Id,
  'IL': States.Il, 'IN': States.In, 'IA': States.Ia, 'KS': States.Ks,
  'KY': States.Ky, 'LA': States.La, 'ME': States.Me, 'MD': States.Md,
  'MA': States.Ma, 'MI': States.Mi, 'MN': States.Mn, 'MS': States.Ms,
  'MO': States.Mo, 'MT': States.Mt, 'NE': States.Ne, 'NV': States.Nv,
  'NH': States.Nh, 'NJ': States.Nj, 'NM': States.Nm, 'NY': States.Ny,
  'NC': States.Nc, 'ND': States.Nd, 'OH': States.Oh, 'OK': States.Ok,
  'OR': States.Or, 'PA': States.Pa, 'RI': States.Ri, 'SC': States.Sc,
  'SD': States.Sd, 'TN': States.Tn, 'TX': States.Tx, 'UT': States.Ut,
  'VT': States.Vt, 'VA': States.Va, 'WA': States.Wa, 'WV': States.Wv,
  'WI': States.Wi, 'WY': States.Wy, 'DC': States.Dc
};

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  onInputChange,
  placeholder = "Enter your address...",
  apiKey = GOOGLE_MAPS_API_KEY,
  value,
  ...textFieldProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const isSelectingRef = useRef(false); // Track if we're in the middle of selecting an address

  // Sync internal input value with external value prop (only when not selecting from typeahead)
  useEffect(() => {
    if (value !== undefined && value !== inputValue && !isSelectingRef.current) {
      setInputValue(value as string);
    }
  }, [value]);

  // Load Google Maps API
  useEffect(() => {
    const loadGoogleMapsAPI = () => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsLoaded(true);
        return;
      }

      // Check if script is already being loaded
      if (document.querySelector(`script[src*="maps.googleapis.com"]`)) {
        // Wait for it to load
        window.initGooglePlaces = () => {
          setIsLoaded(true);
        };
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGooglePlaces`;
      script.async = true;
      script.defer = true;
      
      window.initGooglePlaces = () => {
        setIsLoaded(true);
      };

      document.head.appendChild(script);
    };

    loadGoogleMapsAPI();
  }, [apiKey]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;

    try {
      // Create autocomplete instance
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'US' }, // Restrict to US addresses
          fields: ['address_components', 'formatted_address', 'geometry']
        }
      );

      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', handlePlaceSelect);

    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }

    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded]);

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    
    if (!place || !place.address_components) {
      return;
    }


    // Set flag to prevent external value sync during selection
    isSelectingRef.current = true;

    // Parse address components
    const addressComponents: AddressComponents = {
      formattedAddress: place.formatted_address
    };

    place.address_components.forEach((component: any) => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        addressComponents.streetNumber = component.long_name;
      } else if (types.includes('route')) {
        addressComponents.route = component.long_name;
      } else if (types.includes('locality')) {
        addressComponents.locality = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        // Convert state code to States enum value
        const stateCode = component.short_name;
        addressComponents.administrativeAreaLevel1 = STATE_CODE_TO_STATES_ENUM[stateCode] || component.long_name;
      } else if (types.includes('postal_code')) {
        addressComponents.postalCode = component.long_name;
      } else if (types.includes('country')) {
        addressComponents.country = component.long_name;
      }
    });

    // Update input value with street address only (not full formatted address)
    const streetAddress = [
      addressComponents.streetNumber,
      addressComponents.route
    ].filter(Boolean).join(' ');

    setInputValue(streetAddress || addressComponents.formattedAddress || '');


    // Call the callback with parsed components
    onAddressSelect(addressComponents);

    // Reset flag after a short delay to allow parent updates to complete
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 100);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    onInputChange?.(value);
  };

  return (
    <TextField
      {...textFieldProps}
      inputRef={inputRef}
      value={inputValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      InputProps={{
        ...textFieldProps.InputProps,
        startAdornment: (
          <span style={{ marginRight: '8px', opacity: 0.6 }}>
            {isLoaded ? '📍' : '⏳'}
          </span>
        ),
      }}
      helperText={
        !isLoaded 
          ? "Loading Google Places..." 
          : "Start typing your address for suggestions"
      }
    />
  );
};

export default AddressAutocomplete;